import React, { memo, useState } from 'react';
import { Box, Button, Grid, Typography, CircularProgress, Theme } from '@material-ui/core';
import { Add, AssignmentTurnedIn, ExpandMore, Remove } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { VolunteerCompletedTaskItem } from './VolunteerCompletedTaskItem';
import { useVolunteerCommonProfileContext } from '../../VolunteerCommonProfileContext';
import { sharedStyles } from '../../../../general/activities-v2/ActivityStyles/shared';

export interface CompletedTask {
  completedTaskId: string;
  dateSubmitted: string;
  status: string;
  task: {
    taskId: string;
    title: string;
    adminOnly: boolean;
    dateRemoved?: string | null;
  };
}

const useStyles = makeStyles(() =>
  createStyles({
    list: {
      display: 'flex',
      flexDirection: 'column',
    },
    button: {
      alignItems: 'center',
      '& > span': {
        textTransform: 'none',
      },
      borderRadius: 6,
      height: 38,
    },
  })
);

const useCompletedRemovedTasksStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      border: '1px solid lightgray',
      borderRadius: 6,
      padding: '1rem 1.5rem',
      marginTop: '1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      '& .text': {
        fontSize: 14,
        fontWeight: 500,
        color: theme.color.grey.neutral400,
      },
      '& .button': {
        color: theme.color.grey.neutral400,
        border: 'none',
        textTransform: 'uppercase',
        fontSize: '12px',
        fontWeight: 500,
      },
    },
    subTitle: {
      fontSize: '12px',
      color: theme.color.grey.neutral300,
      paddingTop: '3px',
    },
  })
);

interface CompletedRemovedTasksProps {
  completedRemovedTasks: CompletedTask[];
  preferredName: string;
  userId: string;
  profileId: string;
  refetch: () => void;
}

const CompletedRemovedTasks = (props: CompletedRemovedTasksProps) => {
  const { completedRemovedTasks, preferredName, userId, profileId, refetch } = props;
  const classes = useCompletedRemovedTasksStyles();
  const buttonClasses = sharedStyles.button();
  const [showMore, setShowMore] = useState(false);

  if (completedRemovedTasks.length === 0) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className='text'>{`Removed Tasks (${completedRemovedTasks.length})`}</Typography>
        <Button
          endIcon={showMore ? <Remove style={{ fontSize: '16px' }} /> : <Add style={{ fontSize: '16px' }} />}
          classes={buttonClasses}
          className='button'
          onClick={() => setShowMore(!showMore)}
        >
          Show
        </Button>
      </Box>
      {showMore && (
        <>
          <Typography className={classes.subTitle}>
            These tasks were submitted by or for {preferredName}, but the tasks themselves have since been deleted by a
            volunteer manager. They are kept here for reference and no longer count towards completed tasks.
          </Typography>
          <Box marginTop='0.5rem'>
            {completedRemovedTasks.map((completedTask) => (
              <VolunteerCompletedTaskItem
                key={completedTask.completedTaskId}
                completedTask={completedTask}
                userId={userId}
                profileId={profileId}
                refetch={refetch}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

interface VolunteerTasksSectionProps {
  userId: string;
  profileId: string;
}

export const VolunteerTasksSection = memo((props: VolunteerTasksSectionProps) => {
  const classes = useStyles();
  const { userId, profileId } = props;

  const {
    volunteerNameOptions: { preferredName },
    completedTasks,
    completedRemovedTasks,
    hasMore,
    loadMore,
    loading,
    reloadNewData,
  } = useVolunteerCommonProfileContext();

  const refetch = () => {
    reloadNewData();
  };

  return (
    <Grid container spacing={2} direction='column' style={{ position: 'relative' }}>
      <Grid item xs={12} style={{ textAlign: 'left' }}>
        <Typography style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}>
          <AssignmentTurnedIn style={{ fontSize: '14px', paddingRight: '5px' }} /> Complete Tasks
        </Typography>
        <Typography variant='body2' style={{ color: '#464646' }} align='left'>
          Tasks submitted by or for {preferredName}
          <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '20px 0' }} />
        </Typography>
        {completedTasks.map((completedTask) => (
          <VolunteerCompletedTaskItem
            key={completedTask.completedTaskId}
            completedTask={completedTask}
            userId={userId}
            profileId={profileId}
            refetch={refetch}
          />
        ))}
        {completedTasks.length === 0 && (
          <Typography
            variant='body2'
            style={{ fontSize: '13px', color: '#494949', textAlign: 'center', padding: '2rem 0' }}
          >
            No results found
          </Typography>
        )}
        {hasMore && (
          <Button
            onClick={loadMore}
            style={{ alignSelf: 'center', marginTop: '1rem' }}
            className={classes.button}
            variant='outlined'
            disableElevation
            disabled={loading}
            endIcon={loading ? <CircularProgress size={16} /> : <ExpandMore />}
          >
            {loading ? 'Loading...' : 'Show more'}
          </Button>
        )}
        <CompletedRemovedTasks
          completedRemovedTasks={completedRemovedTasks}
          preferredName={preferredName}
          userId={userId}
          profileId={profileId}
          refetch={refetch}
        />
      </Grid>
    </Grid>
  );
});
